import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, apiClient } from '@/api/apiClient';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Plus,
  Edit,
  Trash2,
  Building2,
  Users,
  Phone,
  Mail,
  MapPin,
  Search,
  Settings,
  User
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import ConfirmDialog from '@/components/ConfirmDialog';
import useConfirmDialog from '@/hooks/useConfirmDialog';

export default function Employers() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [employers, setEmployers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seedingEmployerId, setSeedingEmployerId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingEmployer, setEditingEmployer] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    industry: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    contact_name: '',
    hr_contact_email: '',
    logo_url: '',
    welcome_video_url: '',
    calendly_link: '',
    is_active: true,
    enforce_dependent_verification: false,
    require_evidence_of_insurability_for_late_enrollment: true,
    employer_wait_period_days: 60,
    allow_qle_enrollment: true,
    flex_amount_min: 600,
    flex_amount_max: 2500,
    flex_calculation_increment: 50,
    flex_claimable_percentage: 0.86,
    default_enrollment_window_days: 30,
    qle_enrollment_window_days: 60,
    document_retention_years: 7,
    eoi_coverage_threshold: 50000,
    late_enrollment_grace_days: 30,
    default_admin_fee: 60
  });
  const { confirmDialogProps, openConfirm } = useConfirmDialog();

  useEffect(() => {
    loadData();
  }, []);

  // Lets EmployerDetail's "Edit" button deep-link back here and reopen the
  // edit modal for that employer, instead of duplicating the whole form.
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || employers.length === 0) return;
    const match = employers.find((e) => e.id === editId);
    if (match) {
      openModal(match);
    }
    setSearchParams({}, { replace: true });
  }, [employers]);

  const loadData = async () => {
    try {
      const [employersData, employeesData] = await Promise.all([
        api.entities.Employer.list(),
        api.entities.Employee.list()
      ]);
      setEmployers(employersData);
      setEmployees(employeesData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeCount = (employerId) => {
    return employees.filter(e => e.employer_id === employerId && e.is_active !== false).length;
  };

  const filteredEmployers = employers.filter(employer =>
    employer.company_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openModal = (employer = null) => {
    if (employer) {
      setEditingEmployer(employer);
      setFormData({
        company_name: employer.company_name || '',
        industry: employer.industry || '',
        address: employer.address || '',
        city: employer.city || '',
        state: employer.state || '',
        zip_code: employer.zip_code || '',
        phone: employer.phone || '',
        contact_name: employer.contact_name || '',
        hr_contact_email: employer.hr_contact_email || '',
        logo_url: employer.logo_url || '',
        welcome_video_url: employer.welcome_video_url || '',
        calendly_link: employer.calendly_link || '',
        is_active: employer.is_active !== false,
        enforce_dependent_verification: employer.enforce_dependent_verification || false,
        require_evidence_of_insurability_for_late_enrollment: employer.require_evidence_of_insurability_for_late_enrollment !== false,
        employer_wait_period_days: employer.employer_wait_period_days || 60,
        allow_qle_enrollment: employer.allow_qle_enrollment !== false,
        flex_amount_min: employer.flex_amount_min || 600,
        flex_amount_max: employer.flex_amount_max || 2500,
        flex_calculation_increment: employer.flex_calculation_increment || 50,
        flex_claimable_percentage: employer.flex_claimable_percentage || 0.86,
        default_enrollment_window_days: employer.default_enrollment_window_days || 30,
        qle_enrollment_window_days: employer.qle_enrollment_window_days || 60,
        document_retention_years: employer.document_retention_years || 7,
        eoi_coverage_threshold: employer.eoi_coverage_threshold || 50000,
        late_enrollment_grace_days: employer.late_enrollment_grace_days || 30,
        default_admin_fee: employer.default_admin_fee || 60
      });
    } else {
      setEditingEmployer(null);
      setFormData({
        company_name: '',
        industry: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        phone: '',
        contact_name: '',
        hr_contact_email: '',
        logo_url: '',
        welcome_video_url: '',
        calendly_link: '',
        is_active: true,
        enforce_dependent_verification: false,
        require_evidence_of_insurability_for_late_enrollment: true,
        employer_wait_period_days: 60,
        allow_qle_enrollment: true,
        flex_amount_min: 600,
        flex_amount_max: 2500,
        flex_calculation_increment: 50,
        flex_claimable_percentage: 0.86,
        default_enrollment_window_days: 30,
        qle_enrollment_window_days: 60,
        document_retention_years: 7,
        eoi_coverage_threshold: 50000,
        late_enrollment_grace_days: 30,
        default_admin_fee: 60
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editingEmployer) {
        await api.entities.Employer.update(editingEmployer.id, formData);
        toast.success('Employer updated successfully');
      } else {
        await api.entities.Employer.create(formData);
        toast.success('Employer created successfully');
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save employer');
    }
  };

  const deleteEmployer = async (employer) => {
    try {
      await api.entities.Employer.delete(employer.id);
      toast.success('Employer deleted');
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete employer');
    }
  };

  const confirmDeleteEmployer = (employer) => {
    openConfirm({
      title: 'Delete employer account?',
      description: `${employer.company_name} and its settings will be removed. Employee records stay intact but will be detached from this employer. This action cannot be undone.`,
      confirmLabel: 'Delete employer',
      isDestructive: true,
      onConfirm: () => deleteEmployer(employer)
    });
  };

  const handleSeedStarterBenefits = async (employer) => {
    try {
      setSeedingEmployerId(employer.id);
      const response = await apiClient.post(`/entities/Employer/seed-default-benefits/${employer.id}`);
      const seeded = response?.data?.seeded;
      const categories = response?.data?.categories ?? 0;
      const products = response?.data?.products ?? 0;

      if (seeded) {
        toast.success(`Seeded ${categories} categories and ${products} products`);
      } else {
        toast.info('This employer already has categories; seed skipped');
      }
    } catch (err) {
      console.error(err);
      const apiMessage = err?.response?.data?.error;
      toast.error(apiMessage ? `Failed to seed starter benefits: ${apiMessage}` : 'Failed to seed starter benefits');
    } finally {
      setSeedingEmployerId(null);
    }
  };

  const confirmSeedStarterBenefits = (employer) => {
    openConfirm({
      title: 'Seed starter benefits?',
      description: `Seed default benefit categories and products for ${employer.company_name}. Seeding only runs when the employer has no categories yet.`,
      confirmLabel: 'Seed starter benefits',
      onConfirm: () => handleSeedStarterBenefits(employer)
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Employers</h1>
          <p className="text-slate-500 mt-1">
            Manage company accounts and settings
          </p>
        </div>
        <Button 
          onClick={() => openModal()}
          className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Employer
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search employers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Employers Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredEmployers.map((employer, idx) => (
          <motion.div
            key={employer.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
          >
            <Card
              className="h-full hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/EmployerDetail/${employer.id}`)}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold text-xl">
                    {employer.company_name?.[0] || 'C'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate">
                      {employer.company_name}
                    </h3>
                    <p className="text-sm text-slate-500">{employer.industry || 'Industry not set'}</p>
                    <Badge className="mt-2 bg-emerald-100 text-emerald-700">
                      <Users className="w-3 h-3 mr-1" />
                      {getEmployeeCount(employer.id)} Employees
                    </Badge>
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-sm">
                  {employer.contact_name && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <User className="w-4 h-4 text-slate-400" />
                      {employer.contact_name}
                    </div>
                  )}
                  {employer.hr_contact_email && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Mail className="w-4 h-4 text-slate-400" />
                      {employer.hr_contact_email}
                    </div>
                  )}
                  {employer.phone && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Phone className="w-4 h-4 text-slate-400" />
                      {employer.phone}
                    </div>
                  )}
                  {(employer.city || employer.state) && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      {[employer.city, employer.state].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>

                <div className="mt-4 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={(e) => { e.stopPropagation(); openModal(employer); }}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={(e) => { e.stopPropagation(); confirmSeedStarterBenefits(employer); }}
                    disabled={seedingEmployerId === employer.id}
                  >
                    <Settings className="w-4 h-4 mr-1" />
                    {seedingEmployerId === employer.id ? 'Seeding...' : 'Seed Benefits'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                    onClick={(e) => { e.stopPropagation(); confirmDeleteEmployer(employer); }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingEmployer ? 'Edit Employer' : 'Add New Employer'}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="basic" className="py-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic">
                <Building2 className="w-4 h-4 mr-2" />
                Basic Info
              </TabsTrigger>
              <TabsTrigger value="policies">
                <Settings className="w-4 h-4 mr-2" />
                Policies
              </TabsTrigger>
              <TabsTrigger value="rules">
                <Settings className="w-4 h-4 mr-2" />
                Rules & Limits
              </TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2 space-y-2">
              <Label>Company Name *</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Industry</Label>
              <Input
                value={formData.industry}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Contact Name</Label>
              <Input
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              />
            </div>

            <div className="md:col-span-2 space-y-2">
              <Label>HR Contact Email</Label>
              <Input
                type="email"
                value={formData.hr_contact_email}
                onChange={(e) => setFormData({ ...formData, hr_contact_email: e.target.value })}
              />
            </div>
            
            <div className="md:col-span-2 space-y-2">
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>City</Label>
              <Input
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>State</Label>
              <Input
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>ZIP Code</Label>
              <Input
                value={formData.zip_code}
                onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Logo URL</Label>
              <Input
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
              />
            </div>
            
            <div className="md:col-span-2 space-y-2">
              <Label>Welcome Video URL (YouTube Embed)</Label>
              <Input
                value={formData.welcome_video_url}
                onChange={(e) => setFormData({ ...formData, welcome_video_url: e.target.value })}
                placeholder="https://www.youtube.com/embed/..."
              />
            </div>
            
            <div className="md:col-span-2 space-y-2">
              <Label>Calendly Link</Label>
              <Input
                value={formData.calendly_link}
                onChange={(e) => setFormData({ ...formData, calendly_link: e.target.value })}
                placeholder="https://calendly.com/..."
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="policies" className="space-y-6 mt-4">
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
              <Checkbox
                checked={formData.allow_qle_enrollment}
                onCheckedChange={(checked) => setFormData({ ...formData, allow_qle_enrollment: checked })}
                id="allow_qle"
              />
              <div className="flex-1">
                <Label htmlFor="allow_qle" className="font-semibold cursor-pointer">
                  Allow Qualifying Life Event Enrollments
                </Label>
                <p className="text-sm text-slate-600 mt-1">
                  Employees can enroll mid-year if they experience marriage, birth, divorce, etc.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
              <Checkbox
                checked={formData.enforce_dependent_verification}
                onCheckedChange={(checked) => setFormData({ ...formData, enforce_dependent_verification: checked })}
                id="enforce_dependent"
              />
              <div className="flex-1">
                <Label htmlFor="enforce_dependent" className="font-semibold cursor-pointer">
                  Require Dependent Verification Documents
                </Label>
                <p className="text-sm text-slate-600 mt-1">
                  Employees must upload birth certificates, marriage licenses, etc. for all dependents
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
              <Checkbox
                checked={formData.require_evidence_of_insurability_for_late_enrollment}
                onCheckedChange={(checked) => setFormData({ ...formData, require_evidence_of_insurability_for_late_enrollment: checked })}
                id="require_eoi"
              />
              <div className="flex-1">
                <Label htmlFor="require_eoi" className="font-semibold cursor-pointer">
                  Require Evidence of Insurability for Late Enrollments
                </Label>
                <p className="text-sm text-slate-600 mt-1">
                  Late enrollments need medical underwriting approval before coverage activates
                </p>
              </div>
            </div>

            <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
              <Label>Employer Wait Period (Days)</Label>
              <Input
                type="number"
                value={formData.employer_wait_period_days}
                onChange={(e) => setFormData({ ...formData, employer_wait_period_days: Number.parseInt(e.target.value, 10) || 0 })}
              />
              <p className="text-sm text-slate-600">
                Number of days new hires must wait before becoming eligible for benefits (typically 60 days)
              </p>
            </div>

            <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
              <Label>Default Enrollment Window (Days)</Label>
              <Input
                type="number"
                value={formData.default_enrollment_window_days}
                onChange={(e) => setFormData({ ...formData, default_enrollment_window_days: Number.parseInt(e.target.value, 10) || 0 })}
              />
              <p className="text-sm text-slate-600">
                Days new hires have to complete enrollment after becoming eligible
              </p>
            </div>

            <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
              <Label>QLE Enrollment Window (Days)</Label>
              <Input
                type="number"
                value={formData.qle_enrollment_window_days}
                onChange={(e) => setFormData({ ...formData, qle_enrollment_window_days: Number.parseInt(e.target.value, 10) || 0 })}
              />
              <p className="text-sm text-slate-600">
                Days employees have to enroll after a qualifying life event
              </p>
            </div>

            <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
              <Label>Late Enrollment Grace Period (Days)</Label>
              <Input
                type="number"
                value={formData.late_enrollment_grace_days}
                onChange={(e) => setFormData({ ...formData, late_enrollment_grace_days: Number.parseInt(e.target.value, 10) || 0 })}
              />
              <p className="text-sm text-slate-600">
                Grace period for late enrollments before requiring additional approval
              </p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="rules" className="space-y-6 mt-4">
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
                <Label>Default Admin Fee (Monthly)</Label>
                <div className="flex gap-2">
                  <span className="flex items-center px-3 border rounded-l bg-white">$</span>
                  <Input
                    type="number"
                    value={formData.default_admin_fee}
                    onChange={(e) => setFormData({ ...formData, default_admin_fee: Number.parseFloat(e.target.value) || 0 })}
                    className="rounded-l-none"
                  />
                </div>
                <p className="text-sm text-slate-600">
                  Default monthly admin fee for new employees
                </p>
              </div>

              <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
                <Label>Premium Claimable Percentage</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={formData.flex_claimable_percentage}
                    onChange={(e) => setFormData({ ...formData, flex_claimable_percentage: Number.parseFloat(e.target.value) || 0 })}
                  />
                  <span className="flex items-center px-3 border rounded-r bg-white">x 100%</span>
                </div>
                <p className="text-sm text-slate-600">
                  Percentage of premium balance eligible for wellness claims (86% rule)
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
                <Label>Premium Minimum</Label>
                <div className="flex gap-2">
                  <span className="flex items-center px-3 border rounded-l bg-white">$</span>
                  <Input
                    type="number"
                    value={formData.flex_amount_min}
                    onChange={(e) => setFormData({ ...formData, flex_amount_min: Number.parseFloat(e.target.value) || 0 })}
                    className="rounded-l-none"
                  />
                </div>
              </div>

              <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
                <Label>Premium Maximum</Label>
                <div className="flex gap-2">
                  <span className="flex items-center px-3 border rounded-l bg-white">$</span>
                  <Input
                    type="number"
                    value={formData.flex_amount_max}
                    onChange={(e) => setFormData({ ...formData, flex_amount_max: Number.parseFloat(e.target.value) || 0 })}
                    className="rounded-l-none"
                  />
                </div>
              </div>

              <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
                <Label>Calculation Increment</Label>
                <div className="flex gap-2">
                  <span className="flex items-center px-3 border rounded-l bg-white">$</span>
                  <Input
                    type="number"
                    value={formData.flex_calculation_increment}
                    onChange={(e) => setFormData({ ...formData, flex_calculation_increment: Number.parseFloat(e.target.value) || 0 })}
                    className="rounded-l-none"
                  />
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
                <Label>EOI Coverage Threshold</Label>
                <div className="flex gap-2">
                  <span className="flex items-center px-3 border rounded-l bg-white">$</span>
                  <Input
                    type="number"
                    value={formData.eoi_coverage_threshold}
                    onChange={(e) => setFormData({ ...formData, eoi_coverage_threshold: Number.parseFloat(e.target.value) || 0 })}
                    className="rounded-l-none"
                  />
                </div>
                <p className="text-sm text-slate-600">
                  Coverage amount requiring evidence of insurability
                </p>
              </div>

              <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
                <Label>Document Retention (Years)</Label>
                <Input
                  type="number"
                  value={formData.document_retention_years}
                  onChange={(e) => setFormData({ ...formData, document_retention_years: Number.parseInt(e.target.value, 10) || 0 })}
                />
                <p className="text-sm text-slate-600">
                  Years to retain enrollment and benefit documents
                </p>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-emerald-500 hover:bg-emerald-600">
              {editingEmployer ? 'Update Employer' : 'Create Employer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}